import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const { loginEmail, registerEmail } = useAuth();

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/laboratory";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (mode === "login") await loginEmail(email, password);
      else await registerEmail(email, password, name || email.split("@")[0]);
      nav("/laboratory");
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Authentication failed");
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="auth-page"
      style={{
        minHeight: "calc(100vh - 80px)",
        display: "grid",
        gridTemplateColumns: "1.1fr 1fr",
      }}
    >
      {/* Left aesthetic panel */}
      <div
        style={{
          position: "relative",
          padding: "80px 8vw",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          background:
            "linear-gradient(135deg, rgba(9,10,15,0.92), rgba(30,36,51,0.5)), url('https://images.pexels.com/photos/7190498/pexels-photo-7190498.jpeg') center/cover",
          borderRight: "1px solid rgba(197,160,89,0.2)",
          minHeight: 500,
        }}
      >
        <div className="overline" style={{ marginBottom: 28 }}>Codex · Entry Rite</div>
        <h2 className="font-serif" style={{ fontSize: "clamp(36px, 4.5vw, 56px)", lineHeight: 1.05, marginBottom: 22 }}>
          The gate to the <br />
          <span className="font-display" style={{ color: "var(--brass)" }}>Lyceum</span> opens only
          <br />
          to the <em>disciplined.</em>
        </h2>
        <p className="font-serif" style={{ fontSize: 18, color: "var(--parchment-dim)", fontStyle: "italic", maxWidth: 480, lineHeight: 1.55 }}>
          Identify yourself by Google — or sign your own name into the Codex.
          Your sprints are inscribed and kept.
        </p>
        <div className="font-display" style={{ marginTop: 40, color: "var(--muted)", fontSize: 13 }}>
          Fig. II — the astrolabe of intent.
        </div>
      </div>

      {/* Right form panel */}
      <div style={{ padding: "80px 8vw", display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <div className="overline" style={{ marginBottom: 14 }}>{mode === "login" ? "Return" : "Enroll"}</div>
        <h3 className="font-serif" style={{ fontSize: 40, marginBottom: 30, lineHeight: 1.1 }}>
          {mode === "login" ? "Welcome back, student." : "Enter your name in the Codex."}
        </h3>

        <button
          data-testid="auth-google-btn"
          type="button"
          onClick={handleGoogle}
          className="btn-ghost"
          style={{ width: "100%", justifyContent: "center", padding: "16px", fontSize: 12, marginBottom: 24 }}
        >
          <svg width="16" height="16" viewBox="0 0 48 48">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.8 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.9 6.2 29.2 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16.1 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C33.9 6.2 29.2 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.1 0 9.8-2 13.3-5.2l-6.1-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.3 0-9.8-3.1-11.3-7.9L6 33.7C9.3 40 16.1 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.3 4.1-4.1 5.6l6.1 5.2c-.4.4 6.5-4.8 6.5-14.8 0-1.2-.1-2.4-.4-3.5z"/>
          </svg>
          Continue with Google
        </button>

        <div className="divider-label" style={{ margin: "8px 0 24px" }}>or</div>

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {mode === "register" && (
            <div>
              <label className="label">Name</label>
              <input
                data-testid="auth-name-input"
                className="input-field"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
              />
            </div>
          )}
          <div>
            <label className="label">Email</label>
            <input
              data-testid="auth-email-input"
              type="email"
              required
              className="input-field"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@domain.com"
            />
          </div>
          <div>
            <label className="label">Password</label>
            <input
              data-testid="auth-password-input"
              type="password"
              required
              className="input-field"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>
          {err && (
            <div data-testid="auth-error" className="font-mono" style={{ color: "var(--chaos)", fontSize: 12 }}>
              ⚠ {err}
            </div>
          )}
          <button
            data-testid="auth-submit-btn"
            disabled={busy}
            className="btn-brass"
            style={{ justifyContent: "center", padding: "16px", marginTop: 8 }}
          >
            {busy ? "Summoning..." : mode === "login" ? "Enter" : "Inscribe & Enter"} →
          </button>
        </form>

        <div className="font-mono" style={{ marginTop: 28, fontSize: 12, color: "var(--parchment-dim)" }}>
          {mode === "login" ? (
            <>Not enrolled? <span data-testid="auth-switch-register" className="link-brass" onClick={() => setMode("register")}>Sign the Codex</span></>
          ) : (
            <>Already enrolled? <span data-testid="auth-switch-login" className="link-brass" onClick={() => setMode("login")}>Return</span></>
          )}
        </div>
      </div>
    </div>
  );
}
